import { useReportStateContext } from "../../hooks/useReportState";
import * as styles from "../../styles";
import { ChangeDetailPopover } from "./changeDetails";
import {
  ArrowDropDown as ArrowDropDownIcon,
  BarChart as MetricsIcon,
  Edit as EditIcon,
  FilterList as FilterListIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  SaveAs as SaveAsIcon,
  Search as SearchIcon,
  SwapHoriz as SwapHorizIcon,
  SwapVert as SwapVertIcon,
  Tune as TuneIcon,
  Undo as UndoIcon,
  ViewColumn as ViewColumnIcon,
  Workspaces as WorkspacesIcon,
} from "@mui/icons-material";
import {
  Box,
  Button as MuiButton,
  ButtonGroup,
  Chip,
  ClickAwayListener,
  Grow,
  IconButton,
  MenuList,
  MenuItem,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  INPUT_COLUMN_FILTER_CHANGE_TYPE,
  INPUT_DATE_FILTER_CHANGE_TYPE,
  INPUT_NUMBER_FILTER_CHANGE_TYPE,
} from "components/reportsV3/types/constants";
import * as labels from "config.labels";
import React, { useCallback, useEffect, useRef, useState } from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

export const CHANGE_TYPE_ICONS = {
  columns: ViewColumnIcon,
  columnWidths: SwapHorizIcon,
  sortOrder: SwapVertIcon,
  columnFilter: FilterListIcon,
  dateFilter: TuneIcon,
  numberFilter: TuneIcon,
  [INPUT_COLUMN_FILTER_CHANGE_TYPE]: FilterListIcon,
  [INPUT_DATE_FILTER_CHANGE_TYPE]: TuneIcon,
  [INPUT_NUMBER_FILTER_CHANGE_TYPE]: TuneIcon,
  search: SearchIcon,
  metrics: MetricsIcon,
  rename: EditIcon,
  groupBy: WorkspacesIcon,
};

const ChangeRow = ({
  change,
  columnDisplayNameMap,
  onRevertChange,
  onOpenDetailPopover,
  interactive,
  translateLabels,
}) => {
  const Icon = CHANGE_TYPE_ICONS[change.type];
  return (
    <MenuItem
      sx={{
        py: 1,
        px: 2,
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        "&:hover": { backgroundColor: "action.hover" },
      }}
      disableRipple
    >
      <Box
        sx={{
          color: "primary.main",
          display: "flex",
          alignItems: "center",
          mt: 0.25,
        }}
      >
        {Icon && <Icon fontSize={"small"} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant={"body2"} fontWeight={500} sx={{ mb: 0.25 }}>
          {change.column
            ? columnDisplayNameMap[change.displayColumn ?? change.column] ||
              (change.displayColumn ?? change.column)
            : change.label}
        </Typography>
        {change.filterGroups && change.filterGroups.length > 0 ? (
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}
          >
            {change.filterGroups.map((group) => (
              <Box
                key={group.column}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 0.5,
                }}
              >
                {!change.column && (
                  <Typography
                    variant={"caption"}
                    sx={{ fontWeight: 600, color: "text.secondary" }}
                  >
                    {columnDisplayNameMap[group.column] || group.column}
                  </Typography>
                )}
                {group.chips.map((chip) =>
                  chip.tooltip ? (
                    <Tooltip key={chip.key} title={chip.tooltip} arrow>
                      <Chip
                        label={chip.label}
                        size={"small"}
                        sx={{
                          height: 20,
                          fontSize: "0.7rem",
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Chip
                      key={chip.key}
                      label={chip.label}
                      size={"small"}
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  )
                )}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography
            variant={"caption"}
            color={"text.secondary"}
            sx={{
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {change.description}
          </Typography>
        )}
      </Box>
      {interactive && (
        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
          <Tooltip
            title={translateLabels(labels.SAVE_DROPDOWN_VIEW_DETAILS, {
              default: "View details",
            })}
          >
            <IconButton
              size={"small"}
              onClick={(e) => onOpenDetailPopover(e, change)}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "info.main",
                  backgroundColor: "info.light",
                },
              }}
            >
              <InfoIcon fontSize={"small"} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={translateLabels(labels.SAVE_DROPDOWN_REVERT_THIS_CHANGE, {
              default: "Revert this change",
            })}
          >
            <IconButton
              size={"small"}
              onClick={(e) => {
                e.stopPropagation();
                onRevertChange(change);
              }}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "warning.main",
                  backgroundColor: "warning.light",
                },
              }}
            >
              <UndoIcon fontSize={"small"} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </MenuItem>
  );
};

const TabHeader = ({ label }) => (
  <Box
    sx={{
      px: 2,
      pt: 1,
      pb: 0.25,
      borderTop: 1,
      borderColor: "divider",
      "&:first-of-type": { borderTop: 0 },
    }}
  >
    <Typography
      variant={"caption"}
      fontWeight={700}
      sx={{
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
      }}
    >
      {label}
    </Typography>
  </Box>
);

export const SaveDropdown = ({
  container,
  getChangeDetails,
  onSaveAllChanges,
  isSaving = false,
  onOpenSaveAsDialog,
  readOnly = false,
  allResults,
}) => {
  const {
    hasUnsavedChanges,
    columnDisplayNameMap,
    pendingTrackedChanges: currentChanges,
    handleRevertChange: onRevertChange,
    handleRevertAllChanges: onRevertAllChanges,
    dirtySheetChanges,
    selectedResultIndex,
  } = useReportStateContext();

  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [detailPopoverAnchor, setDetailPopoverAnchor] = useState(null);
  const [detailPopoverChange, setDetailPopoverChange] = useState(null);

  const hasAnyChanges = hasUnsavedChanges || dirtySheetChanges.size > 0;

  const allDirtyEntries = [
    ...(currentChanges?.length > 0
      ? [[selectedResultIndex, currentChanges, true]]
      : []),
    ...Array.from(dirtySheetChanges.entries())
      .filter(([idx]) => idx !== selectedResultIndex)
      .map(([idx, changes]) => [idx, changes, false]),
  ].sort(([a], [b]) => a - b);

  const totalChangeCount = allDirtyEntries.reduce(
    (sum, [, changes]) => sum + changes.length,
    0
  );

  const isMultiTab = allResults != null && allResults.length > 1;
  const showTabHeaders = isMultiTab && allDirtyEntries.length > 1;

  const getTabName = (idx) =>
    allResults?.[idx]?.tableDisplayName || `Sheet ${idx + 1}`;

  useEffect(() => {
    if (!hasAnyChanges) setOpen(false);
  }, [hasAnyChanges]);

  const handleOpenDetailPopover = useCallback((event, change) => {
    event.stopPropagation();
    setDetailPopoverAnchor(event.currentTarget);
    setDetailPopoverChange(change);
  }, []);

  const handleCloseDetailPopover = useCallback(() => {
    setDetailPopoverAnchor(null);
    setDetailPopoverChange(null);
  }, []);

  const handleToggle = () => setOpen((prev) => !prev);

  const handleClose = (event) => {
    if (anchorRef.current?.contains(event.target)) return;
    setOpen(false);
  };

  // "Save as new report" stays reachable when nothing has changed, so any
  // report can be copied; only Save itself needs a change.
  if (!hasAnyChanges && !onOpenSaveAsDialog) return null;

  return (
    <>
      <ButtonGroup
        variant={"contained"}
        ref={anchorRef}
        aria-label={"save report actions"}
        disableElevation
      >
        <MuiButton
          onClick={() => {
            setOpen(false);
            onSaveAllChanges();
          }}
          startIcon={<SaveIcon />}
          loading={isSaving}
          loadingPosition={"start"}
          size={"small"}
          color={"primary"}
          disabled={readOnly || !hasAnyChanges}
          sx={{ whiteSpace: "nowrap" }}
        >
          {totalChangeCount > 0
            ? `${translateLabels(labels.SAVE_DROPDOWN_SAVE, {
                default: "Save",
              })} (${totalChangeCount})`
            : translateLabels(labels.SAVE_DROPDOWN_SAVE, { default: "Save" })}
        </MuiButton>
        <MuiButton
          size={"small"}
          color={"primary"}
          aria-controls={open ? "save-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-label={"open save actions menu"}
          aria-haspopup={"menu"}
          onClick={handleToggle}
          sx={{ px: 0.5, minWidth: "auto" }}
        >
          <ArrowDropDownIcon />
        </MuiButton>
      </ButtonGroup>

      <Popper
        sx={{ zIndex: 1300 }}
        open={open}
        anchorEl={anchorRef.current}
        container={container}
        role={undefined}
        transition
        placement={"bottom-end"}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom-end" ? "right top" : "right bottom",
            }}
          >
            <Paper elevation={3} sx={{ minWidth: 230 }}>
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {totalChangeCount > 0 && (
                    <Box
                      sx={{
                        ...styles.flexBetween,
                        px: 2,
                        py: 1.5,
                        borderBottom: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant={"subtitle2"} fontWeight={600}>
                        {translateLabels(labels.SAVE_DROPDOWN_UNSAVED_CHANGES, {
                          default: "Unsaved Changes",
                        })}
                      </Typography>
                    </Box>
                  )}

                  {allDirtyEntries.length > 0 && (
                    <MenuList id={"save-menu"} sx={{ py: 0.5 }}>
                      {allDirtyEntries.flatMap(([idx, changes, isCurrent]) => [
                        ...(showTabHeaders
                          ? [
                              <TabHeader
                                key={`${idx}:tab`}
                                label={getTabName(idx)}
                              />,
                            ]
                          : []),
                        ...changes.map((change) => (
                          <ChangeRow
                            key={`${idx}:${change.type}:${change.column ?? ""}`}
                            change={change}
                            columnDisplayNameMap={columnDisplayNameMap}
                            onRevertChange={
                              isCurrent ? onRevertChange : undefined
                            }
                            onOpenDetailPopover={
                              isCurrent ? handleOpenDetailPopover : undefined
                            }
                            interactive={isCurrent}
                            translateLabels={translateLabels}
                          />
                        )),
                      ])}
                    </MenuList>
                  )}

                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderTop: totalChangeCount > 0 ? 1 : 0,
                      borderColor: "divider",
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    {hasUnsavedChanges && (
                      <MuiButton
                        variant={"outlined"}
                        size={"small"}
                        startIcon={<UndoIcon />}
                        onClick={onRevertAllChanges}
                        sx={{
                          borderColor: "grey.400",
                          color: "text.primary",
                        }}
                      >
                        {translateLabels(labels.SAVE_DROPDOWN_REVERT_ALL, {
                          default: "Revert All",
                        })}
                      </MuiButton>
                    )}
                    <MuiButton
                      variant={"outlined"}
                      size={"small"}
                      startIcon={<SaveAsIcon />}
                      onClick={() => {
                        setOpen(false);
                        onOpenSaveAsDialog();
                      }}
                      data-testid={"save-as-new-report"}
                      sx={{
                        borderColor: "primary.main",
                        color: "primary.main",
                      }}
                    >
                      {translateLabels(labels.SAVE_DROPDOWN_SAVE_AS, {
                        default: "Save as new report",
                      })}
                    </MuiButton>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <ChangeDetailPopover
        open={Boolean(detailPopoverAnchor)}
        anchorEl={detailPopoverAnchor}
        change={detailPopoverChange}
        fullscreenContainerRef={{ current: container ?? null }}
        getChangeDetails={getChangeDetails}
        onClose={handleCloseDetailPopover}
        onRevertChange={onRevertChange}
      />
    </>
  );
};

export default SaveDropdown;
